import React from 'react';

import { BaseGym, Product } from '../../types/Gym';
import { User } from '../../types/authentication';

import { Button, Column, FormattedPrice, Grid, Row } from '@husslehq/ui-library';

import { ExtraGyms } from '../ExtraGyms/ExtraGyms';

import styles from './PassPickerOnlyMonthlyProduct.module.scss';

type Props = {
  monthlyProduct: Product;
  isTemporarilyClosed?: boolean;
  gym: BaseGym;
  user: User | undefined;
};

export const PassPickerOnlyMonthlyProductBlock: React.FC<Props> = ({
  monthlyProduct,
  isTemporarilyClosed,
  gym,
  user,
}) => {
  const buyProduct = (productType: string, productId: string | undefined, gym: BaseGym) => {
    const clearBasket = ['direct', 'monthly'].includes(productType) ? 1 : 0;
    document.location.href = `/checkout/small-basket-item-add?clear=${clearBasket}&productId=${productId}${
      productType === 'monthly' ? `&productGymId=${gym.id}` : ''
    }`;
  };

  const makeMainGym = () => {
    document.location.href = `/account/change-mplus-gym/${gym.id}`;
  };

  if (monthlyProduct && monthlyProduct.purchasable) {
    const type = monthlyProduct.type;
    const price = monthlyProduct.price;
    const discount = monthlyProduct.discount;
    const id = monthlyProduct.id;
    const purchasable = monthlyProduct.purchasable;

    const isDiscounted = discount && (discount.discounted_price || discount.discounted_price === 0);
    const userPrice = isDiscounted ? discount.discounted_price : price;

    const hasMonthlyWithThisGym = type === 'monthly' && user && user.hasMonthly && user.monthlyGymId === gym.id;
    const hasMonthlyWithAnotherGym = type === 'monthly' && user && user.hasMonthly && user.monthlyGymId !== gym.id;

    const includedGymsFormatted = new Intl.NumberFormat('en-GB').format(gym.included_gyms_count);

    return (
      <div>
        <Grid>
          <Row className={styles['product__picker-row']}>
            <div className={`${styles.product__item} ${monthlyProduct.discount && styles['product--priority-order']}`}>
              <div data-testid={`product-${monthlyProduct.type}`} className={styles.product}>
                {monthlyProduct.discount && <p className={styles.product__label}>You have an offer on this pass</p>}
                <div className={styles.product__upper}>
                  <h3 className={styles.product__heading}>
                    {isDiscounted ? (
                      <>
                        Get {discount.discount_percentage}% off this gym and access to{' '}
                        <a
                          href={`/search/extra-gym?id=${gym.id}&pid=${id}`}
                          className={styles['product__link']}
                          aria-label="View all included gyms"
                        >
                          {includedGymsFormatted}
                        </a>{' '}
                        others with your discount
                      </>
                    ) : (
                      <>
                        Get access to this gym and{' '}
                        <a
                          href={`/search/extra-gym?id=${gym.id}&pid=${id}`}
                          className={styles['product__link']}
                          aria-label="View all included gyms"
                        >
                          {includedGymsFormatted}
                        </a>{' '}
                        others with the Hussle Monthly+ pass
                      </>
                    )}
                  </h3>
                  <ul className={styles['product__list']}>
                    <li>Multi-gym access</li>
                    <li>Unlimited visits</li>
                    <li>No contracts, cancel any time</li>
                    <li>Fitness apps included</li>
                  </ul>
                </div>

                <div className={styles.product__lower}>
                  <div className={styles['product__lower-discount']}>
                    {isDiscounted && <>Get {discount.discount_percentage}% off this pass</>}
                  </div>
                  <div className={styles['product__lower-cta']}>
                    <div>
                      <FormattedPrice
                        className={styles['product__lower-price']}
                        classNamePounds={styles['product__lower-price-pounds']}
                        price={userPrice}
                        originalPrice={isDiscounted ? price : undefined}
                      />{' '}
                      <span className={styles['product__lower-price-qualifier']}>per month</span>
                    </div>
                    {hasMonthlyWithThisGym && <div className={styles.product__primaryGym}>This is your main gym</div>}
                    {hasMonthlyWithAnotherGym && purchasable && (
                      <Button
                        onClick={makeMainGym}
                        disabled={isTemporarilyClosed}
                        level="primary"
                        className={`${styles['product__buy-button']} ${styles['product__make-main-button']}`}
                        fullWidth
                      >
                        Make this my main gym
                      </Button>
                    )}
                    {!hasMonthlyWithAnotherGym && !hasMonthlyWithThisGym && purchasable && (
                      <Button
                        level="primary"
                        disabled={isTemporarilyClosed || !purchasable}
                        className={styles['product__buy-button']}
                        onClick={() => buyProduct(type, id, gym)}
                        fullWidth
                      >
                        Purchase now
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Row>
          <Row className={styles['product__extragyms']}>
            <Column xs="12">
              <ExtraGyms gym={gym} />
            </Column>
          </Row>
        </Grid>
      </div>
    );
  }

  return (
    <div>
      <Grid>
        <Row className={styles['product__picker-row']}>
          <p>
            This venue offers passes which are available exclusively through our corporate and commercial partnerships.
          </p>
        </Row>
      </Grid>
    </div>
  );
};
